/**
 * Ore crystals, drawn from how much ore is actually left.
 *
 * The rest of the map's scatter is baked once into a static mesh, which is
 * right for boulders and grass and wrong for the one thing on the map that
 * gets consumed. A harvester would work a field down to nothing and the field
 * would look exactly as it had at the start; the only way to tell a worked-out
 * patch from a full one was that harvesters stopped going there. That is a
 * player having to infer the state of the economy from the behaviour of their
 * own units.
 *
 * So ore gets its own instanced mesh, and the number of crystals standing in a
 * cell tracks the number in `world.regolith`. A field visibly thins as it is
 * mined and the last crystals wink out as it empties.
 *
 * **Read-only.** This never writes simulation state, and the crystal count is
 * derived, so two clients showing different numbers of crystals would mean the
 * simulations had already diverged.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  InstancedMesh,
  Matrix4,
  MeshLambertMaterial,
  Quaternion,
  Scene,
  Vector3,
} from 'three'
import { World, TERRAIN, REGOLITH_FULL } from '../sim/world.js'
import type { Palette } from '../forge/palette.js'
import { hash2, rampColor } from './terrain.js'

/** Crystals in a cell at full richness. */
const MAX_PER_CELL = 4

/**
 * Cells that can ever hold ore, so a resync scans a few thousand rather than
 * the whole grid. Ore is only ever removed, never added, so this list is
 * complete from the moment the map is generated.
 */
function oreCells(world: World): Int32Array<ArrayBuffer> {
  const out: number[] = []
  for (let i = 0; i < world.cells; i++) {
    if (world.regolith[i] > 0) out.push(i)
  }
  return Int32Array.from(out)
}

export class OreField {
  private mesh: InstancedMesh | null = null
  private cells: Int32Array<ArrayBuffer> = new Int32Array(0)
  /** Crystals currently drawn per tracked cell, so a resync is skipped. */
  private drawn = new Uint8Array(0)
  private readonly matrix = new Matrix4()
  private readonly position = new Vector3()
  private readonly scale = new Vector3()
  private readonly colour = new Color()

  constructor(
    private readonly scene: Scene,
    private readonly palette: Palette,
  ) {}

  /** Ground height under a world position, so crystals sit on the surface. */
  groundAt: (x: number, z: number) => number = () => 0

  build(world: World, attachFog: (m: MeshLambertMaterial) => void): void {
    this.dispose()
    this.cells = oreCells(world)
    this.drawn = new Uint8Array(this.cells.length)
    if (this.cells.length === 0) return

    const material = new MeshLambertMaterial({ flatShading: true })
    attachFog(material)
    const mesh = new InstancedMesh(crystal(), material, this.cells.length * MAX_PER_CELL)
    mesh.castShadow = true
    mesh.receiveShadow = true
    mesh.frustumCulled = false
    mesh.count = 0
    this.mesh = mesh
    this.scene.add(mesh)

    // Colour is filled once per cell rather than per frame: which crystals are
    // *drawn* changes as a field is worked, but a given cell's ore does not
    // turn into a different mineral.
    const tone = {
      regolith: [rampColor(this.palette, 'regolith', 1), rampColor(this.palette, 'regolith', 0.72)],
      gems: [rampColor(this.palette, 'gems', 1), rampColor(this.palette, 'gems', 0.7)],
    }
    for (let n = 0; n < this.cells.length; n++) {
      const ramp = world.terrain[this.cells[n]] === TERRAIN.Gems ? tone.gems : tone.regolith
      for (let k = 0; k < MAX_PER_CELL; k++) {
        const c = ramp[k % 2]
        this.colour.setRGB(c[0], c[1], c[2])
        mesh.setColorAt(n * MAX_PER_CELL + k, this.colour)
      }
    }
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true

    this.drawn.fill(255) // force the first sync to write every cell
    this.sync(world)
  }

  /**
   * Redraw if any tracked cell's crystal count has changed.
   *
   * Cheap enough to call every frame — it is a pass over a few thousand
   * integers and it usually finds nothing — but the caller throttles it
   * anyway, because a field visibly thinning one crystal at a time does not
   * need to be checked sixty times a second.
   */
  sync(world: World): void {
    const mesh = this.mesh
    if (!mesh) return

    let changed = false
    for (let n = 0; n < this.cells.length; n++) {
      if (this.countFor(world, this.cells[n]) !== this.drawn[n]) {
        changed = true
        break
      }
    }
    if (!changed) return

    // Every cell writes into its own fixed block of instances, and the mesh is
    // drawn up to the highest block in use. Compacting instead would be
    // tighter but would break the colours, which are indexed by cell.
    let live = 0
    for (let n = 0; n < this.cells.length; n++) {
      const cell = this.cells[n]
      const count = this.countFor(world, cell)
      this.drawn[n] = count
      const cx = cell % world.size
      const cy = (cell / world.size) | 0
      const base = n * MAX_PER_CELL
      for (let k = 0; k < MAX_PER_CELL; k++) {
        if (k >= count) {
          // Hidden by scaling to nothing, so the block stays aligned.
          this.scale.set(0, 0, 0)
          this.position.set(0, -50, 0)
          this.matrix.compose(this.position, ZERO_ROTATION, this.scale)
          mesh.setMatrixAt(base + k, this.matrix)
          continue
        }
        live = base + k + 1
        const a = hash2(cx * 31 + k * 131, cy * 17 - k * 89)
        const b = hash2(cx - k * 53, cy * 7 + k * 211)
        const x = cx + 0.5 + (a - 0.5) * 0.62
        const z = cy + 0.5 + (b - 0.5) * 0.62
        this.position.set(x, this.groundAt(x, z), z)
        this.scale.set(1, 0.7 + a * 1.1, 1)
        this.matrix.compose(this.position, ZERO_ROTATION, this.scale)
        mesh.setMatrixAt(base + k, this.matrix)
      }
    }
    mesh.count = live
    mesh.instanceMatrix.needsUpdate = true
  }

  /** Crystals to stand in a cell, from how much ore is left in it. */
  private countFor(world: World, cell: number): number {
    const left = world.regolith[cell]
    if (left <= 0) return 0
    // Rounded up, so the last of the ore is still visibly there rather than
    // vanishing while a harvester is on its way to it.
    const share = Math.ceil((left / REGOLITH_FULL) * MAX_PER_CELL)
    return Math.max(1, Math.min(MAX_PER_CELL, share))
  }

  /** Crystals currently standing. The observable the depletion test asserts on. */
  get crystalCount(): number {
    let n = 0
    for (const count of this.drawn) n += count
    return n
  }

  dispose(): void {
    if (!this.mesh) return
    this.scene.remove(this.mesh)
    this.mesh.geometry.dispose()
    this.mesh.dispose()
    this.mesh = null
  }
}

/**
 * Identity rotation, hoisted so `compose` is not handed a new one per crystal.
 *
 * A real `Quaternion`, not an object literal with the same four fields:
 * `Matrix4.compose` reads the underscore-prefixed internals, so a duck-typed
 * stand-in composes to a matrix of NaN and the whole field renders as nothing.
 */
const ZERO_ROTATION = new Quaternion()

/**
 * One crystal: a narrow upright shard, tapered toward the top.
 *
 * Only the three faces the locked camera can see are built, for the same
 * reason the ground scatter cuts the other two — at four of these per cell
 * across a whole field the saving is real.
 *
 * Corner order and index pattern are copied from `MeshBuilder.wedge` rather
 * than improvised. The material is flat-shaded, which means the shader derives
 * its normal from the triangle's own winding and ignores the normal attribute
 * entirely: get the order wrong and every crystal is lit as though it faced
 * away from the sun, which is exactly what the first version looked like —
 * a field of thin dark streaks.
 */
function crystal(): BufferGeometry {
  const w = 0.07
  const h = 0.26
  const tip = 0.016
  const positions: number[] = []
  const normals: number[] = []

  const quad = (corners: number[][], n: number[]): void => {
    for (const i of [0, 1, 2, 0, 2, 3]) {
      positions.push(corners[i][0], corners[i][1], corners[i][2])
      normals.push(n[0], n[1], n[2])
    }
  }

  quad(
    [
      [-tip, h, tip],
      [tip, h, tip],
      [tip, h, -tip],
      [-tip, h, -tip],
    ],
    [0, 1, 0],
  )
  quad(
    [
      [-w, 0, w],
      [w, 0, w],
      [tip, h, tip],
      [-tip, h, tip],
    ],
    [0, 0.4, 1],
  )
  quad(
    [
      [w, 0, w],
      [w, 0, -w],
      [tip, h, -tip],
      [tip, h, tip],
    ],
    [1, 0.4, 0],
  )

  const g = new BufferGeometry()
  g.setAttribute('position', new BufferAttribute(Float32Array.from(positions), 3))
  g.setAttribute('normal', new BufferAttribute(Float32Array.from(normals), 3))
  g.computeBoundingSphere()
  return g
}

/** Terrain kinds that read as ore, for the renderer's own use. */
export function isOreTerrain(kind: number): boolean {
  return kind === TERRAIN.Regolith
}
